import { useEffect, useRef, useState, type ReactNode } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export interface PinnedPanel {
  title: ReactNode;
  body?: ReactNode;
  image?: string;
  imageAlt?: string;
  ctaLabel?: string;
  ctaHref?: string;
  titleSize?: string;
  titleClassName?: string;
}

interface Tokens {
  sectionPanels: string;
  heading: string;
  headingGlow: React.CSSProperties;
  body: string;
  muted: string;
  cardImg: string;
  buttonPrimary: string;
  accent: string;
}

interface Props {
  panels: PinnedPanel[];
  t: Tokens;
}

export default function PinnedProcessSection({ panels, t }: Props) {
  const wrapperRef = useRef<HTMLElement>(null);
  const pinRef = useRef<HTMLDivElement>(null);
  const textRefs = useRef<Array<HTMLDivElement | null>>([]);
  const imgLayerRefs = useRef<Array<HTMLDivElement | null>>([]);
  const progressCircleRef = useRef<SVGCircleElement>(null);
  const [active, setActive] = useState(0);

  const n = panels.length;
  const circumference = 2 * Math.PI * 22;

  useEffect(() => {
    if (typeof window === "undefined" || n === 0) return;
    const wrapper = wrapperRef.current;
    const pin = pinRef.current;
    if (!wrapper || !pin) return;

    const mm = gsap.matchMedia();

    mm.add("(min-width: 768px)", () => {
      // Initial state
      textRefs.current.forEach((el, i) => {
        if (!el) return;
        gsap.set(el, { yPercent: i === 0 ? 0 : 40, autoAlpha: i === 0 ? 1 : 0, force3D: true });
      });
      imgLayerRefs.current.forEach((el, i) => {
        if (!el) return;
        gsap.set(el, {
          clipPath: i === 0 ? "inset(0% 0% 0% 0%)" : "inset(0% 0% 0% 100%)",
          force3D: true,
        });
      });
      if (progressCircleRef.current) {
        gsap.set(progressCircleRef.current, {
          strokeDasharray: circumference,
          strokeDashoffset: circumference,
        });
      }

      // One "unit" of timeline time = one step segment
      const tl = gsap.timeline({
        defaults: { ease: "none" },
        scrollTrigger: {
          trigger: wrapper,
          start: "top top",
          end: () => `+=${window.innerHeight * n}`,
          pin: pin,
          pinSpacing: true,
          anticipatePin: 1,
          scrub: 1.1,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            const idx = Math.min(n - 1, Math.floor(self.progress * n + 0.0001));
            setActive((prev) => (prev === idx ? prev : idx));
          },
        },
      });

      // Continuous progress-ring fill over full timeline
      if (progressCircleRef.current) {
        tl.fromTo(
          progressCircleRef.current,
          { strokeDashoffset: circumference },
          { strokeDashoffset: 0, duration: n },
          0
        );
      }

      // Transitions between steps
      for (let i = 1; i < n; i++) {
        const layer = imgLayerRefs.current[i];
        const prevText = textRefs.current[i - 1];
        const nextText = textRefs.current[i];

        // Transition window: from i-0.35 to i (occupies last 35% of previous segment)
        const start = i - 0.35;

        if (layer) {
          tl.to(
            layer,
            {
              clipPath: "inset(0% 0% 0% 0%)",
              duration: 0.35,
              ease: "power2.inOut",
            },
            start
          );
        }
        if (prevText) {
          tl.to(
            prevText,
            {
              yPercent: -40,
              autoAlpha: 0,
              duration: 0.28,
              ease: "power2.in",
            },
            start
          );
        }
        if (nextText) {
          tl.fromTo(
            nextText,
            { yPercent: 40, autoAlpha: 0 },
            {
              yPercent: 0,
              autoAlpha: 1,
              duration: 0.32,
              ease: "power2.out",
            },
            start + 0.08
          );
        }
      }

      // Ensure the timeline has total length n so scrub maps 1:1 to scroll segments
      tl.to({}, { duration: 0.001 }, n);

      return () => {
        tl.scrollTrigger?.kill();
        tl.kill();
      };
    });

    return () => mm.revert();
  }, [n, circumference]);

  if (n === 0) return null;

  return (
    <section ref={wrapperRef} id="leistung-panels" className={`${t.sectionPanels} relative`}>
      {/* Mobile fallback: simple stacked list */}
      <div className="md:hidden px-6 py-24 space-y-24">
        {panels.map((panel, i) => (
          <div key={i}>
            <span className={`block text-xs uppercase tracking-widest ${t.muted} mb-6 font-medium`}>
              {String(i + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}
            </span>
            <h2
              className={`font-bold uppercase ${t.heading} leading-[0.95] break-words ${panel.titleClassName || ""}`}
              style={{
                fontSize: panel.titleSize || "clamp(1.375rem, 5vw, 3.5rem)",
                letterSpacing: "clamp(-0.4px, -0.12vw, -1.8px)",
                ...t.headingGlow,
              }}
            >
              {panel.title}
            </h2>
            {panel.body && (
              <div className={`mt-6 ${t.body} text-base leading-relaxed`}>{panel.body}</div>
            )}
            {panel.image && (
              <div className={`mt-8 overflow-hidden aspect-[4/3] ${t.cardImg}`}>
                <img src={panel.image} alt={panel.imageAlt || ""} className="w-full h-full object-cover" />
              </div>
            )}
            {panel.ctaLabel && (
              <a
                href={panel.ctaHref || "#"}
                className={`inline-flex items-center mt-8 px-6 py-3.5 rounded-full text-sm font-medium transition-all ${t.buttonPrimary}`}
              >
                {panel.ctaLabel}
              </a>
            )}
          </div>
        ))}
      </div>

      {/* Desktop pinned experience */}
      <div ref={pinRef} className="hidden md:block h-screen w-full overflow-hidden">
        <div className="h-full max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-12 gap-8 md:gap-12 items-center relative">
          {/* LEFT: Text stack */}
          <div className="col-span-5 relative h-[70vh] flex items-center">
            <div className="relative w-full h-full">
              {panels.map((panel, i) => (
                <div
                  key={i}
                  ref={(el) => {
                    textRefs.current[i] = el;
                  }}
                  className="absolute inset-0 flex flex-col justify-center"
                  style={{ willChange: "transform, opacity" }}
                >
                  <span className={`block text-xs uppercase tracking-widest ${t.muted} mb-6 font-medium`}>
                    {String(i + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}
                  </span>
                  <h2
                    className={`font-bold uppercase ${t.heading} leading-[0.95] break-words ${panel.titleClassName || ""}`}
                    style={{
                      fontSize: panel.titleSize || "clamp(1.5rem, 3.6vw, 3.25rem)",
                      letterSpacing: "clamp(-0.4px, -0.12vw, -1.8px)",
                      ...t.headingGlow,
                    }}
                  >
                    {panel.title}
                  </h2>
                  {panel.body && (
                    <div className={`mt-6 ${t.body} text-base md:text-lg leading-relaxed max-w-lg`}>
                      {panel.body}
                    </div>
                  )}
                  {panel.ctaLabel && (
                    <a
                      href={panel.ctaHref || "#"}
                      className={`inline-flex items-center mt-8 px-6 py-3.5 rounded-full text-sm font-medium transition-all self-start ${t.buttonPrimary}`}
                    >
                      {panel.ctaLabel}
                    </a>
                  )}
                </div>
              ))}
            </div>

            {/* Progress ring — bottom left */}
            <div className="absolute bottom-0 left-0 flex items-center gap-4">
              <div className="relative w-14 h-14">
                <svg width="56" height="56" viewBox="0 0 56 56" className="-rotate-90">
                  <circle
                    cx="28"
                    cy="28"
                    r="22"
                    fill="none"
                    stroke="currentColor"
                    strokeOpacity="0.15"
                    strokeWidth="2"
                    className={t.heading}
                  />
                  <circle
                    ref={progressCircleRef}
                    cx="28"
                    cy="28"
                    r="22"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    className={t.accent}
                    style={{
                      strokeDasharray: circumference,
                      strokeDashoffset: circumference,
                    }}
                  />
                </svg>
              </div>
              <span className={`text-xs uppercase tracking-widest ${t.muted} font-medium tabular-nums`}>
                {String(active + 1).padStart(2, "0")} / {String(n).padStart(2, "0")}
              </span>
            </div>
          </div>

          {/* RIGHT: Stacked image layers with curtain wipe */}
          <div className="col-span-7 h-[70vh]">
            <div className={`relative w-full h-full overflow-hidden ${t.cardImg}`}>
              {panels.map((panel, i) => (
                <div
                  key={i}
                  ref={(el) => {
                    imgLayerRefs.current[i] = el;
                  }}
                  className="absolute inset-0"
                  style={{ willChange: "clip-path", zIndex: i }}
                >
                  {panel.image && (
                    <img
                      src={panel.image}
                      alt={panel.imageAlt || ""}
                      className="w-full h-full object-cover"
                      loading={i === 0 ? "eager" : "lazy"}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
